import {Suspense, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate, useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfo} from '@di-shared/api/di/__generated_eu';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  createHistoryFilterSearch,
  createHistoryFilterSearchParams,
  getHistoryFilterPeriodState,
  HISTORY_FILTER_PERIOD,
  type HistoryFilterPeriod,
  type HistoryFilterQueryState,
  parseHistoryFilterSearchParams,
  PERIOD,
} from '@di-shared/features/history/filter/historyFilterQuery';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {addDate, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface FilteredDateHistoryFilterEuProps {
  vin: string;
}

interface FilterModeContentProps {
  filterState: HistoryFilterQueryState;
  replaceFilterState(partialState: Partial<HistoryFilterQueryState>): void;
  onApply(): void;
}

interface HistoryFilterTagsSectionProps {
  selectedTagNames: Array<string>;
  onChangeTagNames(tagNames: Array<string>): void;
}

export default function FilteredDateHistoryFilterEu({vin}: FilteredDateHistoryFilterEuProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterState = useMemo(() => parseHistoryFilterSearchParams(searchParams), [searchParams]);

  function replaceFilterState(partialState: Partial<HistoryFilterQueryState>) {
    const nextState = {
      ...filterState,
      ...partialState,
    };

    setSearchParams(createHistoryFilterSearchParams(nextState), {replace: true});
  }

  function handleApply() {
    navigate(
      {
        pathname: ROUTE_PATHS.HISTORY_FILTERED_RESULT.getFullPath(vin),
        search: createHistoryFilterSearch(filterState),
      },
      {replace: true}
    );
  }
  return (
    <InnerFilterMode
      filterState={filterState}
      replaceFilterState={replaceFilterState}
      onApply={handleApply}
    />
  );
}

function InnerFilterMode({filterState, replaceFilterState, onApply}: FilterModeContentProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const customPeriodMinDate = subDate(today, {days: PERIOD.RECENT_90_DAYS - 1});

  function updatePeriod(period: HistoryFilterPeriod) {
    replaceFilterState({
      ...getHistoryFilterPeriodState(period),
    });
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: filterState.startDate,
        minDate: filterState.endDate ? subDate(filterState.endDate, {days: PERIOD.RECENT_90_DAYS - 1}) : customPeriodMinDate,
        maxDate: filterState.endDate || today,
      }
    );

    if (!date) {
      return;
    }

    replaceFilterState({
      period: HISTORY_FILTER_PERIOD.CUSTOM_DAYS,
      startDate: date,
    });
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: filterState.endDate,
        minDate: filterState.startDate ? filterState.startDate : customPeriodMinDate,
        maxDate:
          filterState.startDate && filterState.startDate < subDate(today, {days: PERIOD.RECENT_90_DAYS - 1})
            ? addDate(filterState.startDate, {days: PERIOD.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    replaceFilterState({
      period: HISTORY_FILTER_PERIOD.CUSTOM_DAYS,
      endDate: date,
    });
  }

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_filter_title_eu')}</span>
        </h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('ctn_mytrips_filter_title_eu')}</h2>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_month')}</h3>
            <div className="area_period">
              <ul className="list_period">
                {[HISTORY_FILTER_PERIOD.PREVIOUS_2_MONTH, HISTORY_FILTER_PERIOD.PREVIOUS_1_MONTH, HISTORY_FILTER_PERIOD.CURRENT_MONTH].map(
                  (period) => {
                    const periodState = getHistoryFilterPeriodState(period);

                    return (
                      <li
                        key={period}
                        className="list_period_item"
                      >
                        <button
                          type="button"
                          className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === period})}
                          onClick={() => updatePeriod(period)}
                        >
                          {periodState.startDate ? CldrDatetimeFormatter.format(periodState.startDate, 'MMM') : ''}
                        </button>
                      </li>
                    );
                  }
                )}
              </ul>
            </div>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_period')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD.RECENT_7_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD.RECENT_7_DAYS)}
                  >
                    7 {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD.RECENT_30_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD.RECENT_30_DAYS)}
                  >
                    30 {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD.CUSTOM_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD.CUSTOM_DAYS)}
                  >
                    {t('ctn_mytrips_download_day_03')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    type="button"
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: filterState.period !== HISTORY_FILTER_PERIOD.CUSTOM_DAYS,
                    })}
                    disabled={filterState.period !== HISTORY_FILTER_PERIOD.CUSTOM_DAYS}
                    onClick={openStartDatePicker}
                  >
                    <span className="body_large_r text_date">
                      {filterState.startDate
                        ? CldrDatetimeFormatter.format(filterState.startDate, 'medium')
                        : t('ctn_mytrips_download_startdate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    type="button"
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: filterState.period !== HISTORY_FILTER_PERIOD.CUSTOM_DAYS,
                    })}
                    disabled={filterState.period !== HISTORY_FILTER_PERIOD.CUSTOM_DAYS}
                    onClick={openEndDatePicker}
                  >
                    <span className="body_large_r text_date">
                      {filterState.endDate
                        ? CldrDatetimeFormatter.format(filterState.endDate, 'medium')
                        : t('ctn_mytrips_download_enddate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <HistoryFilterTagsSection
          selectedTagNames={filterState.tagNames}
          onChangeTagNames={(tagNames) => replaceFilterState({tagNames})}
        />
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() =>
              replaceFilterState({...parseHistoryFilterSearchParams(new URLSearchParams()), sourceDate: filterState.sourceDate})
            }
          >
            {t('ctn_mytrips_filter_reset_eu')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!filterState.startDate || !filterState.endDate}
            onClick={onApply}
          >
            {t('ctn_mytrips_filter_apply_eu')}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryFilterTagsSection(props: HistoryFilterTagsSectionProps) {
  return (
    <section className="section">
      <ErrorBoundary fallback={<HistoryFilterTagsSectionErrorFallback />}>
        <Suspense fallback={<HistoryFilterTagsSectionLoading />}>
          <HistoryFilterTagsSectionInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}

function HistoryFilterTagsSectionInner({selectedTagNames, onChangeTagNames}: HistoryFilterTagsSectionProps) {
  const {t} = useInferableTranslation();

  const {data: tripTags} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfo>>({
    queryKey: [getQueryKey(diApiEu.tripTags.getAllTags)],
    queryFn: () => diApiEu.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  const isAllTagsSelected = selectedTagNames.length === 0;

  function toggleTagSelection(tagName: string) {
    if (selectedTagNames.length === 0) {
      onChangeTagNames([tagName]);
      return;
    }

    if (selectedTagNames.includes(tagName)) {
      const nextTagNames = selectedTagNames.filter((name) => name !== tagName);

      onChangeTagNames(nextTagNames.length > 0 ? nextTagNames : []);
      return;
    }

    onChangeTagNames([...selectedTagNames, tagName]);
  }

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="tag_area">
        <button
          type="button"
          className={clsx('btn btn_round', {'is-selected': isAllTagsSelected})}
          onClick={() => onChangeTagNames([])}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          ></span>
          {t('ctn_mytrips_download_tags_all_eu')}
        </button>
        {tripTags.map(({tagName}) => (
          <button
            key={tagName}
            type="button"
            className={clsx('btn btn_round', {'is-selected': selectedTagNames.includes(tagName)})}
            onClick={() => toggleTagSelection(tagName)}
          >
            <span
              className="ic ic_check_16px"
              aria-hidden="true"
            ></span>
            {tagName}
          </button>
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionHeader() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container_header">
      <h3 className="title_xxsmall">{t('ctn_mytrips_tags_create_title_eu')}</h3>
    </div>
  );
}

function HistoryFilterTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="tag_area">
        {Array.from({length: 4}).map((_, index) => (
          <span
            key={`history-filter-tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="blind">{t('ctn_mytrips_tags_create_title_eu')}</span>
    </>
  );
}

function HistoryFilterTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
          </span>
        </div>
      </div>
    </>
  );
}
